// apps/web/src/components/MediaPanel.js
// SECURITY: Displays media panel with sanitized content

import React, { useState, useEffect } from 'react';
import { Resizable } from 'react-resizable';
import { sanitizeHTML, sanitizeMarkdown } from '../utils/mediaSanitizer';
import 'react-resizable/css/styles.css';
import './MediaPanel.css';

/**
 * MediaPanel - Resizable panel displaying rich media content
 *
 * SECURITY FEATURES:
 * - All HTML is sanitized via DOMPurify
 * - Video embeds restricted to YouTube/Vimeo only
 * - Images validated for safe URLs only
 * - No script execution possible
 *
 * @param {Object} mediaPanel - Sanitized media panel configuration
 * @param {Function} onClose - Callback when panel is closed
 */
export default function MediaPanel({ mediaPanel, onClose }) {
  const [isCollapsed, setIsCollapsed] = useState(
    mediaPanel?.settings?.default_collapsed ?? false
  );

  const [panelSize, setPanelSize] = useState({
    width: parseInt(mediaPanel?.settings?.panel_width) || 400,
    height: parseInt(mediaPanel?.settings?.panel_height) || 600
  });

  if (!mediaPanel || !mediaPanel.enabled) {
    return null;
  }

  const allowResize = mediaPanel.settings?.allow_resize ?? true;

  // Render content based on type
  const renderContent = () => {
    const { content, content_type } = mediaPanel;

    if (!content) return <p className="MediaPanel-empty">No content</p>;

    switch (content_type) {
      case 'html':
      case 'richtext':
        // SECURITY: Already sanitized in mediaSanitizer.sanitizeMediaPanel()
        return (
          <div
            className="MediaPanel-html"
            dangerouslySetInnerHTML={{ __html: sanitizeHTML(content) }}
          />
        );

      case 'markdown':
        // SECURITY: Sanitize markdown to HTML
        return (
          <div
            className="MediaPanel-markdown"
            dangerouslySetInnerHTML={{ __html: sanitizeMarkdown(content) }}
          />
        );

      case 'plaintext':
      default:
        return (
          <div className="MediaPanel-plaintext">
            {content.split('\n').map((line, i) => (
              <p key={i}>{line || '\u00A0'}</p>
            ))}
          </div>
        );
    }
  };

  // Render image attachment
  const renderImage = (attachment) => {
    // SECURITY: URL already validated in sanitizer
    return (
      <div key={attachment.id} className="MediaPanel-image">
        <img
          src={attachment.url}
          alt={attachment.caption || 'Attachment'}
          loading="lazy"
        />
        {attachment.caption && (
          <p className="MediaPanel-caption">{attachment.caption}</p>
        )}
      </div>
    );
  };

  // Render video embed
  const renderVideo = (attachment) => {
    // SECURITY: URL already validated in sanitizer (YouTube/Vimeo only)
    return (
      <div key={attachment.id} className="MediaPanel-video">
        <div className="MediaPanel-video-wrapper">
          <iframe
            src={attachment.embed_url}
            title={attachment.title || 'Video'}
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            referrerPolicy="no-referrer-when-downgrade"
            allowFullScreen
          />
        </div>
        {attachment.title && (
          <p className="MediaPanel-caption">{attachment.title}</p>
        )}
      </div>
    );
  };

  // Render attachments section
  const renderAttachments = () => {
    const { attachments } = mediaPanel;
    if (!attachments || attachments.length === 0) return null;

    return (
      <div className="MediaPanel-attachments">
        {attachments.map(attachment => {
          if (attachment.type === 'image') {
            return renderImage(attachment);
          } else if (attachment.type === 'video') {
            return renderVideo(attachment);
          }
          return null;
        })}
      </div>
    );
  };

  const panelContent = (
    <div className="MediaPanel-container">
      {/* Header */}
      <div className="MediaPanel-header">
        <button
          className="MediaPanel-collapse-btn"
          onClick={() => setIsCollapsed(!isCollapsed)}
          title={isCollapsed ? 'Expand' : 'Collapse'}
        >
          {isCollapsed ? '▶' : '▼'}
        </button>
        <span className="MediaPanel-title">Media Panel</span>
        <button
          className="MediaPanel-close-btn"
          onClick={onClose}
          title="Close"
        >
          ✕
        </button>
      </div>

      {/* Content (hidden when collapsed) */}
      {!isCollapsed && (
        <div className="MediaPanel-body">
          <div className="MediaPanel-content">
            {renderContent()}
          </div>
          {renderAttachments()}
        </div>
      )}
    </div>
  );

  // Render without Resizable for now - debugging
  return (
    <div
      className="MediaPanel"
      style={{
        width: isCollapsed ? 'auto' : `${panelSize.width}px`,
        height: 'auto'
      }}
    >
      {panelContent}
    </div>
  );
}
